import { MONTHS } from 'app-constants';
import { getDocs, Timestamp } from 'firebase/firestore';
import { DashboardRoutePath } from 'modules/dashboard/navigation';
import React from 'react';
import toast from 'react-hot-toast';
import { useHistory } from 'react-router-dom';
import { getRevenueQuery, FilterItem, OrderItem, getArtists } from 'services';
import { Splash } from 'shared/Components';
import FilterControle from 'shared/Components/FlexebleTable/Components/FilterControle';
import FlexebleTable from 'shared/Components/FlexebleTable/FlexebleTable';
import { Option } from 'shared/Components/Select/Select';
import { Text } from 'shared/Typhography';

interface TotalRevenueSchema {
  dateInterval: {
    start?: Timestamp;
    end?: Timestamp;
  };
  artistRevenue: number;
  total: number;
}

export const RevenueTable = () => {
  const history = useHistory();
  const { data: artists, isLoading: isLoadingArtists } = getArtists();

  const [data, setData] = React.useState<any | undefined>();
  const [isLoading, setLoading] = React.useState<boolean>(false);
  const [filters, setFilters] = React.useState<{
    firebase: FilterItem[];
    fields: { [key: string]: any };
  }>({ firebase: [], fields: {} });
  const [ordering, setOrdering] = React.useState<OrderItem[]>([]);
  const [artistOptions, setArtistOptions] = React.useState<Option[]>([]);

  const [totalRevenue, setTotalRevenue] = React.useState<TotalRevenueSchema | undefined>();
  const [isLoadingTotalRevenue, setLoadingTotalRevenue] = React.useState<boolean>(true);
  const fetchTotalRevenue = async () => {
    setLoadingTotalRevenue(true);
    const startDate = filters.firebase.find(
      (filter) => filter.attribute === 'createdAt' && filter.operator === '>=',
    )?.value;
    const endDate = filters.firebase.find(
      (filter) => filter.attribute === 'createdAt' && filter.operator === '<=',
    )?.value;

    try {
      const totalRevenueQuery = getRevenueQuery(filters.firebase, ordering, undefined);

      const totalRevenueDocs = await getDocs(totalRevenueQuery);
      console.log(totalRevenueDocs.docs.length);
      setTotalRevenue({
        dateInterval: {
          start: startDate ? (startDate as Timestamp) : undefined,
          end: endDate ? (endDate as Timestamp) : undefined,
        },
        artistRevenue: totalRevenueDocs.docs.reduce(
          (accumulation, next) => accumulation + next.data().revenue,
          0,
        ),
        total: totalRevenueDocs.docs.reduce(
          (accumulation, next) => accumulation + next.data().totalRevenue,
          0,
        ),
      });
    } catch (err) {
      toast.error('Faild to fetch total revenue');
      console.log(err);
    }
    setLoadingTotalRevenue(false);
  };

  const fetch = async (reset = false) => {
    setLoading(true);
    try {
      const nextQuery = getRevenueQuery(
        filters.firebase,
        ordering,
        data?.length > 0 && !reset ? data[data?.length - 1] : undefined,
      );

      const nextData = await getDocs(nextQuery);
      setData(
        !reset
          ? [...data, ...nextData.docs.filter((doc) => doc.exists())]
          : nextData.docs.filter((doc) => doc.exists()),
      );
    } catch (err) {
      toast.error('Failed to fetch');
      console.log(err);
    }
    setLoading(false);
  };

  const manageOrdering = (attribute: string) => {
    const orderingItem = ordering.find((item) => item.field === attribute);

    if (orderingItem) {
      if (orderingItem.type === 'asc')
        setOrdering(
          ordering.map((item) => (item?.field === attribute ? { ...item, type: 'desc' } : item)),
        );
      else setOrdering(ordering.filter((item) => item?.field !== attribute));

      return;
    }

    setOrdering([...ordering, { field: attribute, type: 'asc' }]);
  };

  React.useEffect(() => {
    if (isLoadingArtists) return;
    setArtistOptions(
      artists?.docs
        ? artists?.docs?.map((item) => {
            return { value: item?.id, label: item?.data()?.Name ?? item?.data()?.name };
          })
        : [],
    );
  }, [isLoadingArtists]);

  React.useEffect(() => {
    if (isLoading) return;

    fetch(true);
  }, [filters.firebase, ordering]);

  React.useEffect(() => {
    fetchTotalRevenue();
  }, [filters.firebase]);

  return (
    <>
      {data && (
        <>
          <FilterControle
            loading={isLoading}
            onChange={(item) => {
              setFilters({
                firebase: [
                  ...filters.firebase.filter((filter) => filter?.attribute !== item?.name),
                  { attribute: item?.name, value: item?.value, operator: '==' },
                ],
                fields: {
                  ...filters.fields,
                  [item.name]: item,
                },
              });
            }}
            clear={(name: string) => {
              setFilters({
                firebase: filters.firebase.filter((item: any) => item?.attribute !== name),
                fields: {
                  ...filters.fields,
                  [name]: undefined,
                },
              });
            }}
            values={filters.fields}
            keys={[
              {
                name: 'createdAt',
                placeholder: 'Date Range',
                options: [],
                type: 'date-range',
                onChange: (dates: [Date | null, Date | null]) => {
                  const start = dates['0'];
                  const end = dates['1'];
                  if (end) {
                    end.setHours(23);
                    end.setMinutes(59);
                    end.setSeconds(59);
                  }

                  if (!start || !end) {
                    if (filters.firebase.find((fil) => fil.attribute === 'createdAt')) {
                      setFilters({
                        firebase: filters.firebase.filter((fil) => fil.attribute !== 'createdAt'),
                        fields: {
                          ...filters.fields,
                          createdAt: undefined,
                        },
                      });
                    }
                    return;
                  }

                  let firebaseFilter: FilterItem[] = filters.firebase;
                  if (start && end) {
                    firebaseFilter.push({
                      value: Timestamp.fromDate(start),
                      attribute: 'createdAt',
                      operator: '>=',
                    });

                    firebaseFilter.push({
                      value: Timestamp.fromDate(end),
                      attribute: 'createdAt',
                      operator: '<=',
                    });
                  } else {
                    firebaseFilter = firebaseFilter.filter(
                      (item) => item?.attribute !== 'createdAt',
                    );
                  }

                  setFilters({
                    firebase: [...firebaseFilter],
                    fields: {
                      ...filters.fields,
                      createdAt: { startDate: start, endDate: end },
                    },
                  });
                },
              },
              {
                name: 'artistId',
                placeholder: 'Select Artist',
                options: artistOptions,
                disabled: isLoadingArtists,
                onChange: (item) =>
                  history.push(
                    `/${DashboardRoutePath.INSIGHTS}/${DashboardRoutePath.ARTIST_INSIGHTS}/${item?.value}`,
                  ),
                onClear: () => history.push(`/${DashboardRoutePath.INSIGHTS}`),
              },
            ]}
          />
          <Text margin="20px 0" size={2} weight="bold">
            Total Revenue
          </Text>
          {!isLoadingTotalRevenue && (
            <FlexebleTable
              data={[totalRevenue]}
              schema={[
                {
                  header: 'Date Interval',
                  key: 'date',
                  mapper: (item: TotalRevenueSchema) => {
                    const parseDate = (date: Date) =>
                      `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;

                    const startDate = item.dateInterval.start
                      ? item.dateInterval.start.toDate()
                      : 'Not Set';
                    const endDate = item.dateInterval.end
                      ? item.dateInterval.end.toDate()
                      : 'Not Set';

                    return `${typeof startDate === 'string' ? startDate : parseDate(startDate)} - ${
                      typeof endDate === 'string' ? endDate : parseDate(endDate)
                    }`;
                  },
                },
                {
                  header: 'Artist Royalties',
                  key: 'artistRevenue',
                  mapper: (item) => `$${(item?.artistRevenue ?? 0).toFixed(5)}`,
                },
                {
                  header: 'Revenue',
                  key: 'total',
                  mapper: (item) => `$${item.total.toFixed(5)}`,
                },
              ]}
            />
          )}
          {isLoadingTotalRevenue && <Splash position="relative" width={64} />}

          <Text margin="20px 0" size={2} weight="bold">
            Revenue Insights
          </Text>
          <FlexebleTable
            onScroll={() => {
              if (!isLoading) fetch();
            }}
            scrollThreshold={10}
            ordering={ordering}
            data={data}
            schema={[
              {
                header: 'Created At',
                key: 'createdAt',
                mapper: (item) => {
                  const date = item?.data()?.createdAt?.toDate();
                  if (!date) return 'Not Set';

                  return `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
                },
                headerClick: () => manageOrdering('createdAt'),
              },
              {
                header: 'Artist Royalties',
                key: 'revenue',
                mapper: (item) => `$${(item.data().revenue ?? 0).toFixed(5)}`,
                headerClick: () => manageOrdering('revenue'),
              },
              {
                header: 'Revenue',
                key: 'totalRevenue',
                mapper: (item) => `$${item.data().totalRevenue.toFixed(5)}`,
                headerClick: () => manageOrdering('totalRevenue'),
              },
              {
                header: 'Actions',
                key: 'actions',
                mapper: (item) => {
                  return (
                    <Text
                      style={{ cursor: 'pointer' }}
                      onClick={() =>
                        history.push(`/${DashboardRoutePath.INSIGHT_DETAILS}/${item?.id}`)
                      }
                      color="#585cea"
                    >
                      Details
                    </Text>
                  );
                },
                style: {
                  column: { maxWidth: '100px' },
                },
              },
            ]}
          />
        </>
      )}
      {isLoading && <Splash position="relative" width={64} />}
    </>
  );
};
